import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { BillDetail, billingApi, ConvertLine, rupees } from '../billing.api';

/** A line being edited: from a proforma (`original` set) or added here as an adjustment. */
interface Line {
  key: string;
  milestoneNumber: number | null;
  description: string;
  /** What the proforma had; null on a line added here. */
  original: number | null;
  /** From which proforma, for the label. */
  from: string | null;
  amount: string;
}

/** 9% of an amount, rounded half-up to paise as the server does. */
const gst = (amount: number, rate: number) => Math.round(amount * rate) / 100;

/**
 * Raise one bill (tax invoice) from one or more open proformas of the same quotation. Each line's
 * amount can be changed and adjustment lines added (negative for a deduction, e.g. an advance);
 * GST is worked out again on the new amounts. Every milestone stays on the bill.
 */
export default function ConvertProformasDialog({
  proformaIds,
  onClose,
  onConverted,
}: {
  /** Open when not empty. */
  proformaIds: number[];
  onClose: () => void;
  onConverted: (bill: BillDetail) => void;
}) {
  const open = proformaIds.length > 0;
  const [proformas, setProformas] = useState<BillDetail[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [billDate, setBillDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const idsKey = proformaIds.join(',');
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setBillDate(dayjs().format('YYYY-MM-DD'));
    setNotes('');
    Promise.all(proformaIds.map((id) => billingApi.get(id)))
      .then((list) => {
        if (cancelled) return;
        const sorted = [...list].sort((a, b) => a.bill.billDate.localeCompare(b.bill.billDate) || a.bill.id - b.bill.id);
        setProformas(sorted);
        setLines(
          sorted.flatMap((p) =>
            p.items.map((i) => ({
              key: `${p.bill.id}-${i.id}`,
              milestoneNumber: i.milestoneNumber,
              description: i.description,
              original: Number(i.amount),
              from: p.bill.billNo,
              amount: String(Number(i.amount)),
            }))
          )
        );
      })
      .catch((e) => {
        if (!cancelled) setLoadError(logError({ message: 'Could not load the proformas', error: e, code: 'CONVERT_LOAD_FAILED' }));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const cgstRate = Number(proformas[0]?.cgstRate ?? 9);
  const sgstRate = Number(proformas[0]?.sgstRate ?? 9);

  // Problems the server would refuse, shown before sending.
  const problems = useMemo(() => {
    const out: string[] = [];
    if (proformas.length > 1) {
      const quote = proformas[0].quoteId;
      if (proformas.some((p) => p.quoteId !== quote)) out.push('These proformas are for different quotations. One bill covers one quotation.');
      if (proformas.some((p) => p.bill.status !== 'ACTIVE')) out.push('Only open proformas can be converted.');
      const seen = new Map<number, string>();
      proformas.forEach((p) =>
        p.items.forEach((i) => {
          if (i.milestoneNumber == null) return;
          const other = seen.get(i.milestoneNumber);
          if (other) out.push(`Milestone ${i.milestoneNumber} is on both ${other} and ${p.bill.billNo}.`);
          else seen.set(i.milestoneNumber, p.bill.billNo);
        })
      );
    }
    lines.forEach((l) => {
      const a = Number(l.amount);
      const name = l.milestoneNumber != null ? `Milestone ${l.milestoneNumber}` : `"${l.description || 'New line'}"`;
      if (l.amount.trim() === '' || Number.isNaN(a)) out.push(`${name} needs an amount.`);
      else if (l.milestoneNumber != null && a <= 0) out.push(`${name} needs an amount above zero.`);
      else if (l.milestoneNumber == null && a === 0) out.push(`${name} needs an amount.`);
      if (l.milestoneNumber == null && !l.description.trim()) out.push('An adjustment line needs a description.');
    });
    return out;
  }, [proformas, lines]);

  const totals = useMemo(() => {
    let amount = 0,
      cgst = 0,
      sgst = 0,
      before = 0;
    lines.forEach((l) => {
      const a = Number(l.amount) || 0;
      amount += a;
      cgst += gst(a, cgstRate);
      sgst += gst(a, sgstRate);
    });
    let advances = 0;
    proformas.forEach((p) => {
      before += Number(p.bill.totalAmount);
      advances += Number(p.bill.received) + Number(p.bill.tds);
    });
    const total = amount + cgst + sgst;
    return { amount, cgst, sgst, total, before, change: total - before, advances };
  }, [lines, proformas, cgstRate, sgstRate]);

  const futureDate = dayjs(billDate).isAfter(dayjs(), 'day');
  const tooSmall = totals.advances > 0 && totals.total + 0.005 < totals.advances;
  const canSubmit =
    !loading && !busy && !loadError && lines.length > 0 && problems.length === 0 && totals.amount > 0 && !futureDate && !tooSmall;

  const update = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addAdjustment = () =>
    setLines((ls) => [
      ...ls,
      { key: `new-${Date.now()}`, milestoneNumber: null, description: '', original: null, from: null, amount: '' },
    ]);

  const submit = async () => {
    setBusy(true);
    try {
      // Always sent, so an edited description goes across too; an unchanged amount keeps its percentage.
      const items: ConvertLine[] = lines.map((l) => ({
        milestoneNumber: l.milestoneNumber,
        description: l.description.trim() || null,
        amount: Number(l.amount),
      }));
      const d = await billingApi.convertMany({ proformaIds: proformas.map((p) => p.bill.id), billDate, items, notes: notes.trim() || undefined });
      toast.success(`Bill ${d.bill.billNo} raised from ${proformas.length === 1 ? 'the proforma' : `${proformas.length} proformas`}`);
      onConverted(d);
    } catch (e) {
      toast.error(logError({ message: 'The bill was not raised', error: e, code: 'CONVERT_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const first = proformas[0]?.bill;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth='lg'>
      <DialogTitle>
        Convert {proformaIds.length === 1 ? 'proforma' : `${proformaIds.length} proformas`} to a tax invoice
        {first && (
          <Typography variant='body2' color='text.secondary'>
            {first.clientName ?? '—'} · {first.jobCode} {first.jobName} · {first.companyName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {loadError && <Alert severity='error'>{loadError}</Alert>}
        {!loading && !loadError && proformas.length > 0 && (
          <Stack gap={2.5}>
            <Typography variant='body2' color='text.secondary'>
              From {proformas.map((p) => `${p.bill.billNo} (${rupees(p.bill.totalAmount)})`).join(', ')}. Change an amount to bill
              more or less than the proforma asked for, or add an adjustment line (a negative amount is a deduction). Amounts are
              before GST; GST is added at {cgstRate}% CGST + {sgstRate}% SGST.
            </Typography>
            {totals.advances > 0 && (
              <Alert severity='info'>
                Advances of {rupees(totals.advances)} were received against{' '}
                {proformas.length === 1 ? 'this proforma' : 'these proformas'}. They move onto the tax invoice as receipts, so its due
                is already reduced — do not deduct them again as a line.
              </Alert>
            )}

            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 260 }}>Description</TableCell>
                  <TableCell align='right'>On proforma</TableCell>
                  <TableCell align='right' sx={{ minWidth: 150 }}>Amount on bill</TableCell>
                  <TableCell align='right'>GST</TableCell>
                  <TableCell align='right'>With GST</TableCell>
                  <TableCell padding='checkbox' />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l) => {
                  const a = Number(l.amount) || 0;
                  const g = gst(a, cgstRate) + gst(a, sgstRate);
                  const changed = l.original != null && l.amount.trim() !== '' && a !== l.original;
                  return (
                    <TableRow key={l.key}>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={l.description}
                          placeholder={l.milestoneNumber == null ? 'e.g. Extra site visit, or Less: rate revision' : undefined}
                          onChange={(e) => update(l.key, { description: e.target.value })}
                          helperText={
                            l.milestoneNumber != null ? `Milestone ${l.milestoneNumber} · ${l.from}` : l.from ? `Line on ${l.from}` : 'Adjustment'
                          }
                          inputProps={{ maxLength: 500 }}
                        />
                      </TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', pt: 2 }}>
                        {l.original != null ? rupees(l.original) : '—'}
                      </TableCell>
                      <TableCell align='right' sx={{ verticalAlign: 'top' }}>
                        <TextField
                          size='small'
                          type='number'
                          value={l.amount}
                          onChange={(e) => update(l.key, { amount: e.target.value })}
                          inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                          helperText={changed ? `${a > l.original! ? '+' : '−'}${rupees(Math.abs(a - l.original!)).replace('₹ ', '₹')}` : ' '}
                          FormHelperTextProps={{ sx: { textAlign: 'right', color: changed ? 'warning.dark' : undefined } }}
                        />
                      </TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', pt: 2 }}>{rupees(g)}</TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', pt: 2 }}>
                        <b>{rupees(a + g)}</b>
                      </TableCell>
                      <TableCell padding='checkbox' sx={{ verticalAlign: 'top', pt: 1 }}>
                        {l.milestoneNumber == null ? (
                          <Tooltip title='Remove this line'>
                            <IconButton size='small' onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                              <Icon icon='mdi:close' />
                            </IconButton>
                          </Tooltip>
                        ) : l.original != null && changed ? (
                          <Tooltip title='Back to the proforma amount'>
                            <IconButton size='small' onClick={() => update(l.key, { amount: String(l.original) })}>
                              <Icon icon='mdi:undo' />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell {...({ colSpan: 6 } as object)}>
                    <Button size='small' startIcon={<Icon icon='mdi:plus' />} onClick={addAdjustment}>
                      Add adjustment line
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Stack direction={{ xs: 'column', md: 'row' }} gap={3} justifyContent='space-between' alignItems={{ md: 'flex-start' }}>
              <Stack gap={2} sx={{ flex: 1, maxWidth: 420 }}>
                <TextField
                  label='Bill date'
                  type='date'
                  size='small'
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                  error={futureDate}
                  helperText={futureDate ? 'A bill cannot be dated in the future.' : ' '}
                />
                <TextField
                  label='Notes on the bill (optional)'
                  size='small'
                  multiline
                  minRows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={proformas.find((p) => p.notes)?.notes ?? undefined}
                  helperText='Left empty, the proforma’s notes are used.'
                  inputProps={{ maxLength: 500 }}
                />
              </Stack>
              <Stack gap={0.5} sx={{ minWidth: 280 }}>
                <Total label='Amount' value={rupees(totals.amount)} />
                <Total label={`CGST (${cgstRate}%)`} value={rupees(totals.cgst)} />
                <Total label={`SGST (${sgstRate}%)`} value={rupees(totals.sgst)} />
                <Total label='Bill total' value={rupees(totals.total)} strong />
                <Total label='Proforma total' value={rupees(totals.before)} muted />
                {totals.advances > 0 && <Total label='Advances received' value={rupees(totals.advances)} muted />}
                {totals.advances > 0 && <Total label='Due on the bill' value={rupees(Math.max(0, totals.total - totals.advances))} strong />}
                {Math.abs(totals.change) >= 0.005 && (
                  <Total
                    label='Difference'
                    value={`${totals.change > 0 ? '+' : '−'}${rupees(Math.abs(totals.change))}`}
                    color='warning.dark'
                  />
                )}
              </Stack>
            </Stack>

            {problems.length > 0 && (
              <Alert severity='warning'>
                {problems.map((p) => (
                  <div key={p}>{p}</div>
                ))}
              </Alert>
            )}
            {tooSmall && (
              <Alert severity='warning'>
                The bill total is less than the advances already received ({rupees(totals.advances)}). Raise the amounts, or delete an
                advance on the proforma first.
              </Alert>
            )}
            {totals.amount <= 0 && lines.length > 0 && problems.length === 0 && (
              <Alert severity='warning'>The bill’s amount must be above zero.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant='contained' onClick={submit} disabled={!canSubmit} startIcon={<Icon icon='mingcute:bill-2-line' />}>
          Raise tax invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Total({ label, value, strong, muted, color }: { label: string; value: string; strong?: boolean; muted?: boolean; color?: string }) {
  return (
    <Stack direction='row' justifyContent='space-between' gap={3}>
      <Typography variant='body2' color={muted ? 'text.secondary' : color}>
        {label}
      </Typography>
      <Typography variant='body2' fontWeight={strong ? 700 : 500} color={muted ? 'text.secondary' : color} sx={{ whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Stack>
  );
}
